// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

// --- RxJs
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';

// --- HS Code
import { environment } from '../../../../environments/environment';
import {
  ApplicationError,
  ErrorMessage,
  IApplicationError,
  RespondentTokenValidationModel,
  RespondentValidationStages,
  TokenValidationErrorTypes,
} from '../../../models';
import { OidcService } from '../account/oidc.service';
import { RespondentValidationBaseService } from './respondent-validation-base.service';

// #endregion

@Injectable({
  providedIn: 'root',
})
export class RespondentValidationService extends RespondentValidationBaseService {
  constructor(
    private http: HttpClient,
    oidcService: OidcService
  ) {
    super(oidcService);
  }

  validateRespondentToken(token: string): void {
    if (!token) {
      return;
    }

    const url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.respondentValidationApi +
      `/${token}`;

    this.http
      .get<RespondentTokenValidationModel>(url)
      .pipe(catchError(this._validationError.bind(this)))
      .subscribe((result) => {
        if (
          result.tokenValidationErrorType === TokenValidationErrorTypes.noError
        ) {
          this._validationStage$.next(
            RespondentValidationStages.authenticating
          );
          this.oidcService.signInRedirect({
            extraQueryParams: {
              acr_values: 'otac:' + result.otac,
            },
          });
          return;
        }
        this._evaluateError(result);
      });
  }

  private _evaluateError(result: RespondentTokenValidationModel): void {

    let errorCode: number = ErrorMessage.respondentsValidationInvalidTokenErrorCode;

    this._validationStage$.next(RespondentValidationStages.tokenError);
    switch (result.tokenValidationErrorType) {
      case TokenValidationErrorTypes.invalidToken:
        break;
      case TokenValidationErrorTypes.tokenIsUsed:
        errorCode = ErrorMessage.respondentsValidationUsedTokenErrorCode;
        break;
      case TokenValidationErrorTypes.tokenIsExpired:
        errorCode = ErrorMessage.respondentsValidationExpiredTokenErrorCode;
        break;
      default:
        this._unexpectedError();
        break;
    }

    result.applicationError = ApplicationError.create(
      errorCode,
      'respondentValidationService',
      false
    );
    result.applicationError.errorHeader = 'errorHeaderTokenError';

    this._respondentValidation$.next(result);
  }

  private _unexpectedError() {
    this._validationStage$.next(RespondentValidationStages.tokenError);
    this._respondentValidation$.next({
      tokenValue: '',
      hasPassword: false,
      tokenValidationErrorType: TokenValidationErrorTypes.unexpectedError,
      locale: '',
      applicationError: {
        errorMessage: 'errorHeaderUnexpected',
        detailedMessage: 'respondentValidationUnexpectedError',
      },
    } as RespondentTokenValidationModel);
  }

  private _validationError(err: any) {
    return of({
      tokenValue: '',
      hasPassword: false,
      tokenValidationErrorType: TokenValidationErrorTypes.unexpectedError,
      locale: '',
      applicationError: {
        errorMessage: 'errorHeaderUnexpected',
        detailedMessage: 'respondentValidationUnexpectedError',
      },
    } as RespondentTokenValidationModel);
  }
}
